import { createApp } from 'vue';
import axios from 'axios';
import 'viewerjs/dist/viewer.css';
import 'moment/dist/locale/cs';

import { ApplicationConfig, useConfig } from './composables/useConfig';
import { configureBrowserClients } from '@tfpl/api-client';
import { useUla } from '@/composables/useUla';
import App from './App.vue';

import {
  LicenseManager,
  ModuleRegistry,
  AllEnterpriseModule,
  provideGlobalGridOptions,
  themeMaterial,
} from 'ag-grid-enterprise';
import { AgChartsCommunityModule } from 'ag-charts-community';
import { useLogger } from '@/composables/useLogger';
import { registerSW } from 'virtual:pwa-register';
import { registerPlugins } from './plugins';

LicenseManager.setLicenseKey(
  'Using_this_{AG_Grid}_Enterprise_key_{AG-077187}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{TechFides_Solutions_s.r.o.}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{TF-ERP}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{TF-ERP}_need_to_be_licensed___{TF-ERP}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{14_April_2026}____[v3]_[01]_MTc3NjEyMTIwMDAwMA==d6cdee5ad48e9cd1a40f5872a31151b3',
);
// Register all enterprise features
ModuleRegistry.registerModules([AllEnterpriseModule.with(AgChartsCommunityModule)]);
// Mark all grids as using legacy themes
provideGlobalGridOptions({
  theme: themeMaterial.withParams({
    spacing: 6,
    headerVerticalPaddingScale: 1.2,
    primaryColor: '#1976D2',
    accentColor: '#1976D2',
  }),
  enableFilterHandlers: true,
});

const { logger } = useLogger();
const { setConfig, getConfig } = useConfig();

if (import.meta.env.MODE !== 'test') {
  const registratorUpdateInterval = 10 * 1000;
  registerSW({
    onRegisteredSW: (swUrl, registration) => {
      if (registration) {
        const updateSW = async () => {
          if (!(!registration.installing && navigator)) return;

          if ('connection' in navigator && !navigator.onLine) return;

          const resp = await fetch(swUrl, {
            cache: 'no-store',
            headers: {
              cache: 'no-store',
              'cache-control': 'no-cache',
            },
          });

          if (resp?.status === 200) {
            await registration.update();
          }
        };

        updateSW().catch((e: unknown) => logger.error(e));
        setInterval(updateSW, registratorUpdateInterval);
      }
    },
  });
}

// Note - ommited express server serving static config EP
const configPromise: Promise<{ data: ApplicationConfig }> = import.meta.env.DEV
  ? Promise.resolve({
      data: {
        SERVER_URL: import.meta.env.VITE_SERVER_URL,
        TENANT: '',
        WEBSOCKET_URL: import.meta.env.VITE_WEBSOCKET_URL,
        WEBSOCKET_COOKIE_DOMAIN: import.meta.env.VITE_WEBSOCKET_COOKIE_DOMAIN ?? 'digitalboostgroup.app',
        REDIRECT_URL: '',
        ULA_URL: import.meta.env.VITE_ULA_URL,
        INSTANCE_TYPE: import.meta.env.VITE_INSTANCE_TYPE,
      },
    })
  : // eslint-disable-next-line no-restricted-syntax -- bootstrap: static config endpoint served by the app's own express layer, not a TF API
    axios.get('/config');

const configResponse = await configPromise;
setConfig(configResponse.data);
logger.debug('Current app config:', getConfig());

const app = createApp(App);

/******************************************************************************
 * OPEN API CLIENT (@tfpl/api-client)
 *
 * Gateway baseURL (SERVER_URL) + per-instance auth interceptor (id token).
 * No onUnauthorized: a single 401 must not log the whole app out (login is
 * driven by ULA).
 *****************************************************************************/
const { getIdToken } = useUla();
configureBrowserClients({
  baseUrl: getConfig().SERVER_URL,
  getIdToken: async () => (await getIdToken()) ?? '',
});

/******************************************************************************
 * VUE Plugins
 *****************************************************************************/
registerPlugins(app);

app.mount('#app');

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
globalThis.app = app;
